import { motion } from "framer-motion";
import { Bot, Sparkles, PhoneCall, CalendarCheck, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CallAnalysisSummary } from "@/types";

interface PostCallReflectionProps {
  callData?: CallAnalysisSummary;
  businessName: string;
}

export function PostCallReflection({ callData, businessName }: PostCallReflectionProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-gradient-to-br from-[#2e2e2e] via-[#2d2d2d] to-[#2c2c2c] border border-white/[0.1] p-6 sm:p-8"
    >
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-lime-500/20 to-lime-400/20 border border-lime-500/20 rounded-lg flex items-center justify-center mx-auto mb-4">
          <Bot className="h-6 w-6 text-lime-400" />
        </div>
        <h3 className="text-xl font-black text-gray-100 mb-1">
          How Did MYNA AI Perform?
        </h3>
        <p className="text-sm text-gray-500">
          Review the AI receptionist's performance for {businessName}
        </p>
      </div>

      {/* Call Summary */}
      {callData?.ai_summary && (
        <div className="bg-[#333333] border border-white/[0.06] rounded-lg p-4 mb-4">
          <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Call Summary</h4>
          <p className="text-sm text-gray-300 leading-relaxed">{callData.ai_summary}</p>
        </div>
      )}

      {/* Key Moments */}
      {callData && (
        <div className="grid gap-2 sm:grid-cols-3 mb-4">
          {callData.booking_made && (
            <KeyMoment
              icon={<CalendarCheck className="h-4 w-4 text-lime-400" />}
              label="Booking Made"
              color="lime"
            />
          )}
          {callData.sentiment_score !== null && callData.sentiment_score >= 70 && (
            <KeyMoment
              icon={<Sparkles className="h-4 w-4 text-lime-300" />}
              label={`Sentiment: ${callData.sentiment_score}/100`}
              color="lime"
            />
          )}
          {callData.duration_seconds > 0 && (
            <KeyMoment
              icon={<PhoneCall className="h-4 w-4 text-gray-400" />}
              label={`${Math.floor(callData.duration_seconds / 60)}:${String(callData.duration_seconds % 60).padStart(2, "0")} call`}
              color="gray"
            />
          )}
        </div>
      )}

      {/* Transparency Note */}
      <div className="bg-lime-500/5 border border-lime-500/10 rounded-lg p-4 mb-6">
        <p className="text-xs text-lime-300/80 leading-relaxed italic">
          "Everything you just heard was generated by artificial intelligence in real-time.
          No scripts, no recordings — pure AI conversation."
        </p>
      </div>

      {/* CTA */}
      <div className="text-center space-y-3">
        <p className="text-sm text-gray-300 font-medium">
          Now imagine this answering <span className="text-lime-400">YOUR</span> phones 24/7 — while you focus on running your business.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            className="bg-gradient-to-r from-lime-400 to-lime-600 text-[#292929] font-bold hover:opacity-90"
            asChild
          >
            <a href="https://cal.com/mannybrar/30min" target="_blank" rel="noopener noreferrer">
              Schedule Walkthrough
              <ArrowRight className="h-4 w-4 ml-1" />
            </a>
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

function KeyMoment({ icon, label, color }: { icon: React.ReactNode; label: string; color: string }) {
  const bgColor = color === "lime" ? "bg-lime-500/10" : "bg-white/5";
  const borderColor = color === "lime" ? "border-lime-500/20" : "border-white/[0.08]";

  return (
    <div className={`flex items-center gap-2 ${bgColor} border ${borderColor} rounded-lg p-3`}>
      {icon}
      <span className="text-xs font-medium text-gray-300">{label}</span>
    </div>
  );
}
